import { createContext, useContext, type ReactNode } from 'react';
import type { ResolvedSignageConfig } from '@/config/types';

/**
 * Exposes the parsed config to the tree, replacing the legacy
 * `app.config.globalProperties.config` / `signageConf` globals.
 *
 * The value is immutable for the page lifetime, so it is passed straight through with no
 * memoisation. `main.tsx` renders this provider only once the config has parsed, which is
 * why `useSignageConfig()` can promise a non-nullable config and widgets need no guards.
 */

const ConfigContext = createContext<ResolvedSignageConfig | null>(null);

type ConfigProviderProps = {
  config: ResolvedSignageConfig;
  children: ReactNode;
};

export function ConfigProvider({ config, children }: ConfigProviderProps) {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useSignageConfig(): ResolvedSignageConfig {
  const config = useContext(ConfigContext);
  if (config === null) {
    throw new Error(
      'useSignageConfig() was called outside <ConfigProvider>. The provider is mounted in ' +
        'src/main.tsx around <App />; a component rendered outside that tree cannot read the config.',
    );
  }
  return config;
}
